import ConversationItem from 'mini-mail-list-store/lib/store/schema/ConversationItem';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import getTableToItemRelationKey from 'mini-mail-list-store/lib/utils/getTableToItemRelationKey';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import mapConversationTypeToConversationItem from '../store-factory/mapConversationTypeToConversationItem';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import TableViewConversationRelation from 'mini-mail-list-store/lib/store/schema/TableViewConversationRelation';
import { action, select } from 'satcheljs/lib/legacy';
import { mapConversationTypeToConversationRelation } from '../store-factory/mapConversationTypeToConversationRelation';
import { ObservableMap } from 'mobx';
import { prepareMeetingDataForConversationAndGetShouldShowRSVP } from 'mini-mail-list-rsvp/lib/utils/prepareMeetingDataForConversationAndGetShouldShowRSVP';

export interface AddOrUpdateConversationDataState {
    conversationItems: ObservableMap<string, ConversationItem>;
    tableViewConversationRelations: ObservableMap<string, TableViewConversationRelation>;
}

interface RsvpInfoForRow {
    shouldShowRSVP: boolean;
    rsvpUnitAriaDetails: string;
}

/**
 * Adds or updated the conversation in the tableView, tableViewConversationRelations and tableItemViewStateRelations
 * This is called whenever a data is fetched from the server and is being stored. This is also called when
 * a rowAdd or rowModified notification occurs
 * @param conversation to add or update
 * @param tableId where to add or update the conversation
 * @param state for unit testing
 */
export default select<AddOrUpdateConversationDataState>({
    conversationItems: () => listViewStore.conversationItems,
    tableViewConversationRelations: () => listViewStore.tableViewConversationRelations,
})(
    action('addOrUpdateConversationData')(function addOrUpdateConversationData(
        conversation: ConversationType,
        tableView: TableView,
        state?: AddOrUpdateConversationDataState
    ) {
        const { conversationItems, tableViewConversationRelations } = state;

        const conversationId = conversation.ConversationId.Id;
        const tableConversationRelationKey = getTableToItemRelationKey(
            conversationId,
            tableView.id
        );

        // Do not add the conversation if last modified time is less than the time when the folder was last emptied.
        if (tableView.lastEmptiedTime > new Date(conversation.LastModifiedTime)) {
            return;
        }

        const conversationItem = mapConversationTypeToConversationItem(
            conversation,
            tableView.tableQuery
        );
        conversationItems.set(conversationId, conversationItem);

        const newTableConversationRelation = mapConversationTypeToConversationRelation(
            conversation
        );

        const shouldShowRSVP = prepareMeetingDataForConversationAndGetShouldShowRSVP(
            conversation,
            tableView
        );
        preserveOldRsvpInfoIfAny(
            newTableConversationRelation,
            tableViewConversationRelations.get(tableConversationRelationKey),
            { shouldShowRSVP, rsvpUnitAriaDetails: '' }
        );

        tableViewConversationRelations.set(
            tableConversationRelationKey,
            newTableConversationRelation
        );
    })
);

const preserveOldRsvpInfoIfAny = (
    newTableConversationRelation: TableViewConversationRelation,
    oldTableConversationRelation: TableViewConversationRelation,
    defaultRsvpInfoForRow: RsvpInfoForRow
) => {
    const { shouldShowRSVP, rsvpUnitAriaDetails } =
        oldTableConversationRelation || defaultRsvpInfoForRow;

    newTableConversationRelation.shouldShowRSVP = shouldShowRSVP;
    newTableConversationRelation.rsvpUnitAriaDetails = rsvpUnitAriaDetails;
};
